import type { CellContext, Getter, RowData } from '@tanstack/react-table';
import type { UserObject } from '@/lib/heyapi';
import { TableCell } from '@/components/ui/table';
import {
  toDateTimeString,
  formatDateWithSlashes,
  formatDateWithSlashesNoShift,
} from '@/lib/utils/dates';
import { TransactionStatus, TransactionType } from '@/lib/heyapi/types.gen';
import { snakeToTitleCase } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils/currency';
import { formatDistanceToNow } from 'date-fns';
import { TEXT_PLACEHOLDER } from '../layout/Header/utils';

export function CreatedBy({
  getValue,
}: {
  getValue: Getter<UserObject | null>;
}) {
  const user = getValue();

  const nameParts = [user?.firstName, user?.middleName, user?.lastName].filter(
    Boolean
  );
  const name = nameParts.join(' ');
  return <TableCell>{name ?? TEXT_PLACEHOLDER}</TableCell>;
}

export function CreatedByContent({
  getValue,
}: {
  getValue: Getter<UserObject | null>;
}) {
  const user = getValue();

  const nameParts = [user?.firstName, user?.middleName, user?.lastName].filter(
    Boolean
  );
  const name = nameParts.join(' ');
  return name ?? TEXT_PLACEHOLDER;
}

export function DateTimeCell({ getValue }: { getValue: Getter<Date> }) {
  const date = getValue();
  const formattedDateTime = toDateTimeString(date);
  return <TableCell>{formattedDateTime}</TableCell>;
}

export function DateTimeCellContent({ getValue }: { getValue: Getter<Date> }) {
  const date = getValue();
  const formattedDateTime = toDateTimeString(date);
  return formattedDateTime;
}

export function DateCell({ getValue }: { getValue: Getter<Date> }) {
  const date = getValue();
  const formattedDate = formatDateWithSlashes(date);
  return <TableCell>{formattedDate}</TableCell>;
}

export function DateCellTimeStamp({ getValue }: { getValue: Getter<Date> }) {
  const date = getValue();
  const formattedDate = formatDistanceToNow(date, { addSuffix: true });
  return <TableCell>{formattedDate}</TableCell>;
}

/**
 * DateCell variant that prevents timezone shifting issues
 */
export function DateCellNoShift({ getValue }: { getValue: Getter<Date> }) {
  const date = getValue();
  const formattedDate = formatDateWithSlashesNoShift(date);
  return <TableCell>{formattedDate}</TableCell>;
}

export interface CheckBoxCellProps {
  getValue: Getter<boolean>;
  text?: string;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
}

export function CheckBoxCell({
  text = undefined,
  getValue,
  onChange = () => {},
  disabled,
}: CheckBoxCellProps) {
  const isChecked = getValue();
  return (
    <TableCell className="text-nowrap">
      <label>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
        />
        {text && <span className="ml-2">{text}</span>}
      </label>
    </TableCell>
  );
}

interface CheckBoxEditableCellProps<T> {
  onChangeCallback: (
    value: boolean,
    row: CellContext<T, boolean>['row']
  ) => void;
  text?: string;
  disabled?: boolean;
}

export function CheckBoxEditableCell<T extends RowData>({
  onChangeCallback,
  ...cellProps
}: CheckBoxEditableCellProps<T>) {
  return ({ getValue, row }: CellContext<T, boolean>) => (
    <CheckBoxCell
      getValue={getValue}
      {...cellProps}
      onChange={(value) => onChangeCallback(value, row)}
    />
  );
}

export const transactionTypeDisplayText = (
  tType: TransactionType,
  status: TransactionStatus
) => {
  let displayText: string = TEXT_PLACEHOLDER;

  /**
   * Logic Table for Policy Transaction Types & Status Mapping
   *
   * | Transaction Type      | Status         | Shown on table           |
   * |--------------------- |--------------- |------------------------ |
   * | POLICY_BOUND          | IN_PROGRESS    | Not Bound                |
   * | POLICY_BOUND          | ACTIVE         | Policy Bound             |
   * | POLICY_BOUND          | (Any other)    | Not Bound                |
   *
   * | POLICY_CANCELLED      | IN_PROGRESS    | Midterm (Cancellation)   |
   * | POLICY_CANCELLED      | ACTIVE         | Policy Cancelled         |
   * | POLICY_CANCELLED      | (Any other)    | Policy Bound             |
   * |                       |                |                          |
   * | POLICY_ENDORSEMENT    | IN_PROGRESS    | Midterm (Endorsement)    |
   * | POLICY_ENDORSEMENT    | ACTIVE         | Policy (Endorsed)        |
   * | POLICY_ENDORSEMENT    | (Any other)    | Policy Bound             |
   * |                       |                |                          |
   * | POLICY_REINSTATED     | IN_PROGRESS    | Midterm (Reinstate)      |
   * | POLICY_REINSTATED     | ACTIVE         | Policy Reinstated        |
   * | POLICY_REINSTATED     | (Any other)    | Policy Bound             |
   */

  if (tType === TransactionType.POLICY_BOUND) {
    if (status === TransactionStatus.ACTIVE) {
      displayText = TransactionType.POLICY_BOUND;
    } else {
      displayText = 'Not Bound';
    }
  } else if (tType === TransactionType.POLICY_ENDORSEMENT) {
    if (status === TransactionStatus.IN_PROGRESS) {
      displayText = 'Midterm (Endorsement)';
    } else if (status === TransactionStatus.ACTIVE) {
      displayText = 'Policy (Endorsed)';
    } else {
      displayText = TransactionType.POLICY_BOUND;
    }
  } else if (tType === TransactionType.POLICY_CANCELLED) {
    if (status === TransactionStatus.IN_PROGRESS) {
      displayText = 'Midterm (Cancellation)';
    } else if (status === TransactionStatus.ACTIVE) {
      displayText = TransactionType.POLICY_CANCELLED;
    } else {
      displayText = TransactionType.POLICY_BOUND;
    }
  } else if (tType === TransactionType.POLICY_REINSTATED) {
    if (status === TransactionStatus.IN_PROGRESS) {
      displayText = 'Midterm (Reinstate)';
    } else if (status === TransactionStatus.ACTIVE) {
      displayText = TransactionType.POLICY_REINSTATED;
    } else {
      displayText = TransactionType.POLICY_BOUND;
    }
  } else {
    displayText = tType;
  }

  return displayText;
};

/**
 * Cell component for displaying transaction types with specific formatting based on transaction status
 */
export function TransactionTypeCell({ row }: { row: any }) {
  const tType = row.getValue('transactionType') as TransactionType;
  const status = (
    row.getValue('transactionStatus') as string
  ).toLowerCase() as TransactionStatus;

  const displayText = transactionTypeDisplayText(tType, status);

  return (
    <div className="flex">
      <div className="flex items-center justify-center text-nowrap rounded-[100px] px-[11px] py-[4px]">
        {snakeToTitleCase(displayText)}
      </div>
    </div>
  );
}

export function CurrencyCell({
  getValue,
}: {
  getValue: Getter<number | null>;
}) {
  const value = getValue() || 0;
  const formattedValue = formatCurrency(value);
  return <TableCell>{formattedValue}</TableCell>;
}
